/**
 * 레이드 설정 정보
 * 새로운 레이드 추가 시 이 파일만 수정하면 됩니다.
 */

import { CSSProperties } from 'react';

export interface RaidConfig {
  /** 레이드 식별자 (내부용) */
  id: string;
  /** 표시될 레이드 이름 */
  displayName: string;
  /** 매칭에 사용할 키워드들 (레이드 타이틀에 포함되어 있는지 확인) */
  keywords: string[];
  /** 라이트 모드 왼쪽 accent 색상 (hex) */
  lightColor: string;
  /** 다크 모드 왼쪽 accent 색상 (hex) */
  darkColor: string;
  /** 카테고리 정보 (스케줄 생성 시 그룹화) */
  category?: string;
}

/**
 * 레이드 설정 목록
 * 라이트/다크 모드 모두 지원
 */
export const RAID_CONFIGS: RaidConfig[] = [
  // 어비스 레이드
  {
    id: 'kamen',
    displayName: '카멘',
    keywords: ['카멘'],
    lightColor: '#add7f9',
    darkColor: '#6b9bc9',
  },
  {
    id: 'ivory-tower',
    displayName: '상아탑',
    keywords: ['상아탑'],
    lightColor: '#ffd3ae',
    darkColor: '#d19966',
  },
  {
    id: 'iliakan',
    displayName: '일리아칸',
    keywords: ['일리아칸', '일리'],
    lightColor: '#b7ffa5',
    darkColor: '#7ab85c',
  },
  {
    id: 'abrelshud',
    displayName: '아브렐슈드',
    keywords: ['아브렐슈드', '아브렐'],
    lightColor: '#ade8ff',
    darkColor: '#5ca3c9',
  },
  {
    id: 'abrel-short',
    displayName: '아브',
    keywords: ['아브'],
    lightColor: '#dec8ff',
    darkColor: '#9c7ac9',
  },
  {
    id: 'kayangel',
    displayName: '카양겔',
    keywords: ['카양겔', '카양'],
    lightColor: '#fffdbc',
    darkColor: '#c9ba66',
  },
  {
    id: 'kouku-saton',
    displayName: '쿠크세이튼',
    keywords: ['쿠크세이튼', '쿠크'],
    lightColor: '#fbc2c2',
    darkColor: '#c97a7a',
  },
  {
    id: 'brelshaza',
    displayName: '비아키스',
    keywords: ['비아키스', '비아'],
    lightColor: '#ffc3ec',
    darkColor: '#c77ab8',
  },

  // 군단장 레이드
  {
    id: 'valtan',
    displayName: '발탄',
    keywords: ['발탄'],
    lightColor: '#c6ffef',
    darkColor: '#5cc9ad',
  },

  // 기타
  {
    id: 'behemoth',
    displayName: '베히모스',
    keywords: ['베히모스'],
    lightColor: '#a3c4ff',
    darkColor: '#7a8fc9',
  },

   // 카제로스 레이드
   {
    id: 'echidna',
    displayName: '에키드나',
    keywords: ['에키드나'],
    lightColor: '#ffd2ef',
    darkColor: '#c77ba8',
  },
  {
    id: 'egir',
    displayName: '에기르',
    keywords: ['에기르'],
    lightColor: '#ffb2a2',
    darkColor: '#d17a66',
  },
  {
    id: 'kazeroth',
    displayName: '카제로스',
    keywords: ['카제로스'],
    lightColor: '#f59393',
    darkColor: '#c95c5c',
  },
  {
    id: 'armoche',
    displayName: '아르모체',
    keywords: ['아르모체'],
    lightColor: '#f3e7ab',
    darkColor: '#c9ad66',
  },
  {
    id: 'mordum',
    displayName: '모르둠',
    keywords: ['모르둠'],
    lightColor: '#f9f782',
    darkColor: '#c9ba47',
  },
  {
    id: 'serka',
    displayName: '세르카',
    keywords: ['세르카'],
    lightColor: '#ffc387',
    darkColor: '#d99551',
  },

  // 지평의 성당
  {
    id: 'cathedral',
    displayName: '지평의 성당',
    keywords: ['지평의 성당', '성당'],
    lightColor: '#fef08a',
    darkColor: '#c9b95a',
  },

  // 벨가르딘
  {
    id: 'belgardin',
    displayName: '벨가르딘',
    keywords: ['벨가르딘'],
    lightColor: '#bda7ff',
    darkColor: '#9479ef',
  },
];

/**
 * 레이드 카테고리 인터페이스
 */
export interface RaidCategory {
  /** 카테고리 식별자 */
  id: string;
  /** 카테고리 제목 */
  title: string;
  /** 카테고리에 속한 레이드 seq 목록 ('others'는 나머지 모든 레이드) */
  raidSeqs: number[];
}

/**
 * 레이드 카테고리 목록 (스케줄 생성 시 사용)
 * 최신 레이드부터 순서대로 정의
 */
export const RAID_CATEGORIES: RaidCategory[] = [
  { id: 'free', title: '자유모집', raidSeqs: [] },
  { id: 'belgardin', title: '죽음의 계율자, 벨가르딘', raidSeqs: [44, 43, 42] },
  { id: 'cathedral', title: '지평의 성당', raidSeqs: [41, 40, 39] },
  { id: 'serka', title: '고통의 마녀, 세르카', raidSeqs: [38, 37, 36] },
  { id: 'finale', title: '종막: 최후의 날', raidSeqs: [34, 33] },
  { id: 'act4', title: '4막: 파멸의 성채', raidSeqs: [32, 31] },
  { id: 'trio', title: '3종 숙제 모음', raidSeqs: [103, 102, 101, 100] },
  { id: 'others', title: '나머지 레이드', raidSeqs: [26, 25, 24, 23, 22, 21, 20, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0] },
];

/**
 * 레이드 타이틀에 맞는 왼쪽 accent 색상을 반환합니다.
 * @param raidTitle 레이드 타이틀 (예: "노mal 에키드나", "하드 발탄")
 * @param isDark 다크 모드 여부
 * @returns 왼쪽 border 색상 hex 값
 */
export const getRaidColor = (raidTitle: string, isDark: boolean = false): string => {
  // keywords 순서대로 매칭 시도 (더 구체적인 키워드가 먼저 매칭되도록)
  const raid = RAID_CONFIGS.find((config) =>
    config.keywords.some((keyword) => raidTitle.includes(keyword))
  );

  if (!raid) {
    return isDark ? '#6b7280' : '#9ca3af'; // 매칭 안되면 gray
  }

  return isDark ? raid.darkColor : raid.lightColor;
};

/**
 * 레이드 카드에 적용할 왼쪽 accent 스타일을 반환합니다.
 * @param raidTitle 레이드 타이틀
 * @param isDark 다크 모드 여부
 * @returns CSSProperties 객체
 */
export const getRaidAccentStyle = (raidTitle: string, isDark: boolean = false): CSSProperties => {
  const color = getRaidColor(raidTitle, isDark);
  return {
    borderLeft: `4px solid ${color}`,
  };
};

/**
 * 레이드 ID로 설정을 찾습니다.
 * @param id 레이드 ID
 * @returns RaidConfig 또는 undefined
 */
export const getRaidConfigById = (id: string): RaidConfig | undefined => {
  return RAID_CONFIGS.find((config) => config.id === id);
};

/**
 * 레이드 타이틀로 설정을 찾습니다.
 * @param raidTitle 레이드 타이틀
 * @returns RaidConfig 또는 undefined
 */
export const getRaidConfigByTitle = (raidTitle: string): RaidConfig | undefined => {
  return RAID_CONFIGS.find((config) =>
    config.keywords.some((keyword) => raidTitle.includes(keyword))
  );
};
